import React from 'react';
import Carousel from '../components/Carousel';
import { usePageLastUpdated } from '../hooks/usePageLastUpdated';
import './ResearchPage.css';

const PAGE_LAST_UPDATED = { dateTime: '2016-03-30', displayDate: 'March 30, 2016' };

const ResearchPage = () => {
  usePageLastUpdated(PAGE_LAST_UPDATED);

  const carouselSlides = [
    {
      backgroundImage: '/images/banner_phd_slides.png',
      content: (
        <>
          <p>I defended my doctoral dissertation</p>
          <p>
            ago, and the slides of my doctoral defense{' '}
            <a
              href="/research/phd/presentation/index.html"
              title="Watch the presentation slides online."
            >
              presentation
            </a>{' '}
            are now available online.
          </p>
        </>
      ),
    },
    {
      backgroundImage: '/images/banner_phd.png',
      content: (
        <>
          <p>
            I defended my doctoral dissertation, titled{' '}
            <a
              href="/research/phd/dissertation/index.html"
              title="Read my doctoral dissertation online."
            >
              Peer-to-Peer Television for the IP Multimedia Subsystem
            </a>
            , at{' '}
            <a
              href="https://www.uc3m.es/"
              title="Go the web site of the Carlos III University."
              target="_blank"
              rel="noopener"
              className="external"
            >
              Carlos III University of Madrid
            </a>
            .
          </p>
          <p>
            The defense took place on July 18, 2012 at 11:00 AM in{' '}
            <a
              href="/research/phd/map/index.html"
              title="See the map with the location of my doctoral defense."
              target="_blank"
              rel="noopener"
            >
              Aula de Grados
            </a>{' '}
            from the Padre Soler Building, Leganés Campus.
          </p>
          <p>
            <a
              className="btn btn--primary"
              role="button"
              href="/research/phd/index.html"
              title="Go to the page of my doctoral defense."
            >
              Go to the Defense Page
            </a>
          </p>
        </>
      ),
    },
  ];

  const resources = [
    {
      date: 'May 2012',
      title: 'Peer-to-Peer Television for the Next Generation Networks',
      description:
        'Invited speaker at the Networking Technology and Strategies (NeTS) Research Group from Universitat Pompeu Fabra',
      download: { href: '/download/UPFTalk20120509Slides.pptx', label: 'Download (PPTX/2.99 MB)' },
    },
    {
      date: 'November 2010',
      title: 'Optimal source selection in P2P IPTV based on abandon probability',
      description:
        'A probabilistic method for reducing peer churn in P2P IPTV based on TV channel characteristics',
      download: { href: '/download/IPTVPeerSelectionPoster.pdf', label: 'Download (PDF/254 KB)' },
    },
    {
      date: 'February 2010',
      title: 'Peer-to-Peer vs. IP multicast: comparing approaches to IPTV streaming based on TV channel popularity',
      description:
        'A comparison between IP multicast and P2P as alternatives for IPTV streaming, presented during the IMDEA Networks/NETCOM seminar',
      download: { href: '/download/IMDEASeminar2Slides.pptx', label: 'Download (PPTX/2.09 MB)' },
    },
    {
      date: 'March 2009',
      title: 'P2P content distribution for multimedia services in IMS-based telco environments',
      description:
        'An introduction in multimedia content distribution with the IP Multimedia Subsystem presented during the IMDEA Networks/NETCOM seminar',
      download: { href: '/download/IMDEASeminar1Slides.pptx', label: 'Download (PPTX/10.7 MB)' },
    },
    {
      date: 'February 2009',
      title: 'An overview of IPTV standardization in a TISPAN next generation network',
      description: null,
      download: { href: '/download/IptvTispanOverviewSlides.pdf', label: 'Download (PDF/728 KB)' },
    },
    {
      date: 'February 2008',
      title: 'Video streaming with peer-to-peer multicast',
      description:
        'An introduction in application layer multicast and a brief presentation of several proposals such as Splitstream, P2PCast and PeerCast',
      download: { href: '/download/P2pMulticastIntroSlides.pdf', label: 'Download (PDF/5.28 MB)' },
    },
  ];

  return (
    <>
      <Carousel slides={carouselSlides} />
      <div className="research-page">
        <h1>Resources</h1>
        <table className="publication padding-4px align-top">
          <tbody>
            {resources.map((item, index) => (
              <tr key={index}>
                <td style={{ width: '100px' }}>
                  <p className="text-small text-italic text-gray">{item.date}</p>
                </td>
                <td>
                  <p>
                    <b>{item.title}</b>
                  </p>
                  {item.description && (
                    <p className="text-small text-italic">{item.description}</p>
                  )}
                  <p className="text-small">
                    <a href={item.download.href} target="_blank" rel="noopener">
                      {item.download.label}
                    </a>
                  </p>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <h1>Academia</h1>
        <h2>Doctoral degree</h2>
        <p>
          <a href="/research/phd/index.html">Go to the defense page</a>
        </p>

        <h2>Master degree</h2>
        <p>
          <a href="/download/MscThesis.pdf" target="_blank" rel="noopener">
            Download thesis
          </a>{' '}
          (PDF/908 KB) |{' '}
          <a href="/download/MscSlides.pdf" target="_blank" rel="noopener">
            Download slides
          </a>{' '}
          (PDF/6.80 MB)
        </p>

        <h2>Engineering degree</h2>
        <p>
          Advisors:{' '}
          <a
            href="https://users.utcluj.ro/~dobrota/"
            target="_blank"
            rel="noopener"
            className="external"
          >
            Virgil Dobrotă
          </a>
          ,{' '}
          <a
            href="https://people.ac.upc.edu/jordid/"
            target="_blank"
            rel="noopener"
            className="external"
          >
            Jordi Domingo-Pascual
          </a>
        </p>
        <p>
          <a href="/download/BscThesis.pdf" target="_blank" rel="noopener">
            Download thesis
          </a>{' '}
          (PDF/2.52 MB) |{' '}
          <a href="/download/BscSlides.pps" target="_blank" rel="noopener">
            Download slides
          </a>{' '}
          (PPS/12.9 MB)
        </p>
      </div>
    </>
  );
};

export default ResearchPage;
